import { Autocomplete, Button, Chip, IconButton, Popper, Stack, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridFilterModel, GridSortModel, GridToolbarContainer } from "@mui/x-data-grid-pro";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useCallback, useRef } from "react";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { useDisableKoBiddingManualMaxCpcMutation, useUpdateKoBiddingManualMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import { FilterPanelCustom } from "../filter-panel-custom";
import ModalDeactivateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-deactivate-strategy";
import { useEnforceMaxCPCMutation } from "src/context/api/liveBidding/api";
import EnforceMaxCpcModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-enforce-max-cpc";
import { formatNumber, fPercent } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";
import ModalCreateStrategyCampaigns from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy-campaigns";

interface PercentageChangeProps {
    value: number | null;
    inverse?: boolean;
    rounded?: number;
    format?: string;
}

type FormatType = "percentage" | "pp";

const PercentageChange: React.FC<PercentageChangeProps & { format?: FormatType }> = ({
    value,
    inverse = false,
    rounded = 0,
    format = "percentage",
}) => {
    if (value === null || value === undefined) return null;

    const suffix = format === "pp" ? "pp" : "%";

    const isZeroAfterRound = Number(value.toFixed(rounded)) === 0;

    const isDash = value === 0 || isZeroAfterRound;

    const color =
        isDash
            ? "rgba(145, 158, 171, 1)"
            : value > 0
                ? (inverse ? "rgba(220, 24, 24, 1)" : "rgba(0, 162, 91, 1)")
                : value < 0
                    ? (inverse ? "rgba(0, 162, 91, 1)" : "rgba(220, 24, 24, 1)")
                    : "rgba(145, 158, 171, 1)";

    const style: React.CSSProperties = {
        color,
        marginLeft: "8px",
        textAlign: "right",
        fontVariantNumeric: "lining-nums tabular-nums",
        fontFamily: 'var(--font-family-Font-1, "Public Sans")',
        fontSize: "14px",
        lineHeight: "22px",
    };

    if (value > 1000) {
        return <span style={style}>{`>999${suffix}`}</span>;
    }

    if (value < -1000) {
        return <span style={style}>{`<-999${suffix}`}</span>;
    }

    const roundedValue = Number(value.toFixed(rounded));

    if (roundedValue === 0) {
        return <span style={style}>-</span>;
    }

    const sign = roundedValue > 0 ? "+" : "";

    return (
        <span style={style}>
            {sign}
            {roundedValue}
            {suffix}
        </span>
    );
};

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    selectedDateRange?: { label: string; value: string };
    onDateRangeChange?: (newRange: { label: string; value: string }) => void;
    selectedCompareDate?: any;
    onCompareDateChange?: (newRange: { label: string; value: string }) => void;
    refetch: any,
}

export default function DataGridCampaigns({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    onDateRangeChange,
    onCompareDateChange,
    selectedCompareDate,
    selectedDateRange,
    refetch
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([])
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const apiRef: any = useRef();
    const toolbarRef = useRef<HTMLDivElement>(null);

    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();

    const dateRangeOptions = [
        { label: 'Last 7 Days', value: 'last7d' },
        { label: 'Last 14 Days', value: 'last14d' },
        { label: 'Last 30 Days', value: 'last30d' },
    ];

    const comparisonPeriodOptions = [
        { label: 'Prev. Period', value: 'previous_period' },
        { label: 'Last Year', value: 'last_year' },
    ];

    const [modalCreateStrategy, setModalCreateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalDeactivateStrategy, setModalDeactivateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [confirmDisable, setConfirmDisable] = useState<{ open: boolean; row: any | null, mode: string | null }>({
        open: false,
        row: null,
        mode: null
    });

    const [openEnforceModal, setOpenEnforceModal] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });


    const ActivationCell = ({
        params,
        setModalActivationManual,
        setConfirmDisable,
        setModalCreateStrategy,
    }: any) => {

        const VALID_MODE = "KO Bidding Manual";

        const details = params.row.activations_details ?? [];

        const activeDetails = details.filter(
            (d: any) => d.status === true && d.mode?.startsWith(VALID_MODE)
        );

        const hasAnyMode = activeDetails.length > 0;

        const hasManualStrategy = activeDetails.some(
            (d: any) => d.mode?.startsWith("KO Bidding Manual")
        );

        const canCreateStrategy = !hasManualStrategy;

        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const [activeDetail, setActiveDetail] = useState<any>(null);

        const timeoutRef = useRef<any>(null);

        const handleEnter = (e: React.MouseEvent<HTMLElement>, detail: any) => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            setAnchorEl(e.currentTarget);
            setActiveDetail(detail);
        };

        const handleLeave = () => {
            timeoutRef.current = setTimeout(() => {
                setAnchorEl(null);
                setActiveDetail(null);
            }, 120);
        };

        const open = Boolean(anchorEl);

        const modeLabel = "Manual";

        return (
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    overflow: "hidden",
                    px: 1,
                }}
            >
                {hasAnyMode ? (
                    activeDetails.map((detail: any, i: number) => (
                        <Typography
                            key={i}
                            onMouseEnter={(e) => handleEnter(e, detail)}
                            onMouseLeave={handleLeave}
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                py: 0.4,
                                px: 0.5,
                                height: 28,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                borderRadius: "6px",
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: "pointer",
                                color: "rgba(48, 57, 176, 1)",
                                background: "rgba(111, 120, 247, 0.19)",
                            }}
                        >
                            <Iconify icon="mingcute:pencil-3-line" width={16} />

                            <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                                Manual
                            </Box>
                        </Typography>
                    ))
                ) : (
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
                        --
                    </Typography>
                )}

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top"
                    style={{ zIndex: 2000 }}
                    modifiers={[
                        {
                            name: "offset",
                            options: { offset: [0, 8] },
                        },
                    ]}
                >
                    <Box
                        onMouseEnter={() => {
                            if (timeoutRef.current) clearTimeout(timeoutRef.current);
                        }}

                        onMouseLeave={handleLeave}
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            p: 1,
                            background: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.2,
                                py: 0.6,
                                borderRadius: "8px",
                                background: "rgba(111, 120, 247, 0.19)",
                                color: "rgba(48, 57, 176, 1)",
                            }}
                        >
                            <Iconify icon="mingcute:pencil-3-line" width={16} />

                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                {modeLabel}
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.stopPropagation();

                                setConfirmDisable({
                                    open: true,
                                    row: params.row,
                                    mode: "KO Bidding Manual",
                                });

                                setAnchorEl(null);
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M5.25 1.75H8.75M1.75 3.5H12.25M11.0833 3.5L10.6742 9.63625C10.6129 10.5569 10.5822 11.0172 10.3833 11.3663C10.2083 11.6735 9.94422 11.9206 9.62597 12.0748C9.26448 12.25 8.80314 12.25 7.88045 12.25H6.11955C5.19686 12.25 4.73552 12.25 4.37403 12.0748C4.05577 11.9206 3.79172 11.6735 3.61666 11.3663C3.41781 11.0172 3.38713 10.5569 3.32575 9.63625L2.91667 3.5" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    </Box>
                </Popper>

                {canCreateStrategy && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setModalCreateStrategy({
                                open: true,
                                row: [params.row],
                            });
                        }}
                        sx={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 22,
                            height: 22,
                            borderRadius: "6px",
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            opacity: 0,
                            transition: "all 0.2s ease",
                            ".MuiDataGrid-cell:hover &": {
                                opacity: 1,
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: 20, lineHeight: 1 }}>
                            +
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "campaign_name",
            sortable: true,
            disableColumnMenu: true,
            headerClassName: "keyword-header",
            headerName: "Campaign",
            flex: 1,
            minWidth: 260,

            renderHeader: () => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Campaign
                    </Typography>

                    <CustomTooltip title={"Ad clicks"}
                        placement="top"
                        arrow>
                        <Iconify
                            sx={{ cursor: 'help' }}
                            icon="proicons:cursor-click"
                            width={20}
                            height={20}
                            className="keyword-header-icon"
                        />
                    </CustomTooltip>
                </Box>
            ),

            renderCell: ({ value, row }) => {
                const values = Array.isArray(value)
                    ? value
                    : value
                        ? [value]
                        : [];

                return (
                    <CustomTooltip
                        title={values.join(", ")}
                        placement="top"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    px: "12px",
                                    py: "8px",
                                    borderRadius: "8px",
                                    backgroundColor: "#FCFCFD",
                                    boxShadow: "1px 3px 8px 0 rgba(0, 0, 0, 0.08)",
                                },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                height: 60,
                                px: "12px",
                                minWidth: 0,
                                overflow: "hidden",
                                boxSizing: "border-box",
                                "&:hover": {
                                    backgroundColor: "#F7F8F9",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: "14px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    color: "rgba(51, 60, 71, 1)",
                                    cursor: "help",
                                }}
                            >
                                {(values || []).join(", ")}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "rgba(99, 115, 129, 1)",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                    marginLeft: "12px",
                                }}
                            >
                                {formatNumber(row.clicks)}
                            </Typography>
                        </Box>
                    </CustomTooltip>
                );
            },
        },
        {
            field: "campaign_id",
            type: 'number',
            align: 'left',
            maxWidth: 120,
            flex: 1,
            valueGetter: (params) => {
                return String(params).replace(/\s+/g, '');
            },
            headerAlign: 'left',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign ID
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Campaign ID",
            sortable: false
        },
        {
            field: "campaign_type",
            type: 'string',
            headerAlign: 'left',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign Type
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Campaign Type",
            minWidth: 130,
            sortable: false
        },
        {
            field: "campaign_primary_status_reasons",
            type: 'string',
            align: 'left',
            maxWidth: 260,
            flex: 1,
            headerAlign: 'left',
            valueGetter: (params: any) => {
                const value = params;

                if (!value) return '';

                const list = Array.isArray(value)
                    ? value
                    : JSON.parse(value.replace(/'/g, '"'));

                return list
                    .map((item: any) =>
                        item
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c: any) => c.toUpperCase())
                    )
                    .join(', ');
            },
            renderHeader: () => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Status
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Status",
            sortable: false
        },
        {
            field: "platform",
            headerName: "Platform",
            minWidth: 140,
            sortable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Platform
                    </Typography>
                </Box>
            ),
            type: "singleSelect",
            valueOptions: [
                { value: "GOOGLE", label: "Google Ads" },
                { value: "BING", label: "Bing Ads" },
            ],

            valueGetter: (params: any) => (params || "").toUpperCase(),

            renderCell: (params) => {
                const map: any = {
                    GOOGLE: "Google Ads",
                    BING: "Bing Ads",
                };

                return map[params.value] || params.value;
            },
        },
        {
            field: "bidding_strategy_type",
            type: 'string',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Bidding Strategy Type
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Bidding Strategy Type",
            maxWidth: 200,
            flex: 1,
            sortable: true,
            filterable: true,
        },
        {
            field: "mode",
            headerName: "Mode",
            disableColumnMenu: true,
            filterable: true,
            minWidth: 120,
            maxWidth: 120,
            width: 120,
            sortable: false,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                        flexDirection: "column"
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Mode
                    </Typography>
                </Box>
            ),
            editable: false,
            filterOperators: [
                {
                    label: "is any of",
                    value: "isAnyOf",
                    getApplyFilterFn: () => null,

                    InputComponent: (props) => {
                        const { item, applyValue } = props;

                        return (
                            <Autocomplete
                                sx={{ mt: 2.5 }}
                                multiple
                                options={[
                                    "KO Bidding Manual"
                                ]}
                                value={item.value || []}
                                onChange={(event, newValue) => {
                                    applyValue({
                                        ...item,
                                        value: newValue,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        placeholder="Select modes"
                                    />
                                )}
                                size="small"
                            />
                        );
                    },
                },
            ],
            renderCell: (params) => (
                <ActivationCell
                    params={params}
                    setConfirmDisable={setConfirmDisable}
                    setModalCreateStrategy={setModalCreateStrategy}
                />
            )
        },
        {
            disableColumnMenu: true,
            field: "max_cpc",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: 'left',
            headerName: "Set Max CPC",
            minWidth: 80,
            editable: true,


            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                    sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                    }}
                >
                    Set Max CPC
                </Typography>
            ),

            renderEditCell: (params) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value =
                    koManual?.max_cpc ??
                    koManual?.max_cpc_portfolio_strategy ??
                    null;

                if (!koManual) {
                    return (
                        <Box display="flex" width="100%" justifyContent="flex-end">
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        sx={{
                            height: "100%",
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            defaultValue={value}
                            autoFocus
                            fullWidth
                            onChange={(event) => {
                                let value = event.target.value;
                                value = value.replace(",", ".");

                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: "max_cpc",
                                    value,
                                });
                            }}
                            sx={{
                                width: "100%",

                                "& .MuiInputBase-root": {
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                },

                                "& input": {
                                    padding: "0 12px",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                },

                            }}
                        />
                    </Box>
                );
            },

            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value =
                    koManual?.max_cpc ??
                    koManual?.max_cpc_portfolio_strategy ??
                    null;

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Typography color="rgba(145, 158, 171, 1)">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        onClick={() => {
                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            cursor: "pointer",
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: "100%",
                                height: "65%",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                px: 2,
                                borderRadius: "6px",
                                border: "1px solid rgba(145, 158, 171, 0.20)",
                                background: "rgba(255, 255, 255, 0.01)",
                                fontSize: "0.875rem",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                boxSizing: "border-box",

                                cursor: "pointer",

                                transition: "all 0.15s ease",

                                "&:hover": {
                                    border: "1px solid var(--color-azure-58, #338AF3)",
                                    boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                },
                            }}
                        >
                            {formatCurrency(value, workspace?.currency, lang, true, 2)}
                        </Box>
                    </Box>
                );
            },
        },
        {
            disableColumnMenu: true,
            field: "target_impression_share_position",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: "left",
            headerName: "Position",
            minWidth: 180,
            editable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                >
                    Position
                </Typography>
            ),
            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value = koManual?.target_impression_share_position ?? null;

                const isDisabled =
                    params?.row?.bidding_strategy_type === "Target CPA" ||
                    params?.row?.bidding_strategy_type === "Maximize Conversion Value" ||
                    params?.row?.bidding_strategy_type === "Target ROAS";

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        onClick={() => {
                            if (isDisabled) return;

                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            cursor: isDisabled ? "default" : "pointer",
                        }}
                    >
                        {!isDisabled
                            ? <Box
                                sx={{
                                    width: "100%",
                                    height: "65%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    px: 2,
                                    borderRadius: "6px",
                                    border: "1px solid rgba(145, 158, 171, 0.20)",
                                    backgroundColor: "rgba(255,255,255,0.02)",
                                    transition: "all 0.15s ease",
                                    userSelect: "none",

                                    minWidth: 0,
                                    "&:hover": {
                                        border: isDisabled
                                            ? "1px solid rgba(145, 158, 171, 0.20)"
                                            : "1px solid #338AF3",
                                        boxShadow: isDisabled
                                            ? "none"
                                            : "0 0 0 2px rgba(51, 138, 243, 0.25)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,

                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",

                                        display: "block",
                                        textAlign: "right",

                                        fontSize: "0.875rem",
                                    }}
                                >
                                    {value ?? "-"}
                                </Box>
                            </Box>
                            : <Box
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Typography color="#333C47">-</Typography>
                            </Box>
                        }
                    </Box>
                );
            },
            renderEditCell: (params: any) => {
                const isDisabled =
                    params?.row?.bidding_strategy_type === "Target CPA" ||
                    params?.row?.bidding_strategy_type === "Maximize Conversion Value" ||
                    params?.row?.bidding_strategy_type === "Target ROAS";

                const options = [
                    "Anywhere on results page",
                    "Top of results page",
                    "Absolute top of results page",
                ];

                if (isDisabled) {
                    return (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#999",
                                fontSize: "0.875rem",
                            }}
                        >
                            Not editable for this strategy
                        </Box>
                    );
                }

                return (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            px: 1,
                        }}
                    >
                        <Autocomplete
                            freeSolo
                            fullWidth
                            options={options}
                            value={params.value ?? ""}
                            onChange={(_, newValue) => {
                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value: newValue,
                                });
                            }}
                            onInputChange={(_, newInputValue) => {
                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value: newInputValue,
                                });
                            }}
                            renderInput={(inputParams) => (
                                <TextField
                                    {...inputParams}
                                    autoFocus
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        position: "relative",
                                        top: 10,
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            height: 38,
                                            display: "flex",
                                            alignItems: "center",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                );

            },
        },
        {
            disableColumnMenu: true,
            field: "target_impression_share",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: 'left',
            headerName: "Target IS",
            minWidth: 80,
            editable: true,
            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                >
                    Target IS
                </Typography>
            ),
            renderEditCell: (params) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value =
                    koManual?.target_impression_share ??
                    null;

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        sx={{
                            height: "100%",
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            defaultValue={value}
                            autoFocus
                            fullWidth
                            onChange={(event) => {
                                let value = event.target.value;
                                value = value.replace(",", ".");

                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: "target_impression_share",
                                    value,
                                });
                            }}
                            sx={{
                                width: "100%",

                                "& .MuiInputBase-root": {
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                },

                                "& input": {
                                    padding: "0 12px",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                },

                            }}
                        />
                    </Box>
                );
            },
            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value =
                    koManual?.target_impression_share ??
                    null;

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                const isDisabled =
                    params?.row?.bidding_strategy_type === "Target CPA" ||
                    params?.row?.bidding_strategy_type === "Maximize Conversion Value" ||
                    params?.row?.bidding_strategy_type === "Target ROAS"


                return (
                    <Box
                        onClick={() => {
                            if (isDisabled) return;

                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            cursor: isDisabled ? "default" : "pointer",

                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: "100%",
                                height: "65%",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                px: isDisabled ? 0 : 2,
                                borderRadius: "6px",
                                background: "rgba(255, 255, 255, 0.01)",
                                fontSize: "0.875rem",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                boxSizing: "border-box",

                                border: isDisabled
                                    ? ""
                                    : "1px solid rgba(145, 158, 171, 0.20)",

                                transition: "all 0.15s ease",

                                "&:hover": isDisabled
                                    ? {}
                                    : {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                    },
                            }}
                        >
                            {!isDisabled
                                ? fPercent(value)
                                : <Box
                                    sx={{
                                        display: "flex",
                                        width: "100%",
                                        height: "100%",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <Typography color="#333C47">-</Typography>
                                </Box>
                            }
                        </Box>
                    </Box>
                );
            },
        },
        // TARGET CPA
        {
            disableColumnMenu: true,
            field: "target_cpa",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: 'left',
            headerName: "Target CPA",
            minWidth: 80,
            editable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                >
                    Target CPA
                </Typography>
            ),

            renderEditCell: (params) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value =
                    koManual?.target_cpa ??
                    koManual?.target_cpa_portfolio_strategy ??
                    null;

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        sx={{
                            height: "100%",
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            defaultValue={value}
                            autoFocus
                            fullWidth
                            onChange={(event) => {
                                let value = event.target.value;
                                value = value.replace(",", ".");

                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: "target_cpa",
                                    value,
                                });
                            }}
                            sx={{
                                width: "100%",

                                "& .MuiInputBase-root": {
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                },

                                "& input": {
                                    padding: "0 12px",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                },

                            }}
                        />
                    </Box>
                );
            },

            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value =
                    koManual?.target_cpa ??
                    koManual?.target_cpa_portfolio_strategy ??
                    null;

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                const isDisabled =
                    params?.row?.bidding_strategy_type === "Target ROAS" ||
                    params?.row?.bidding_strategy_type === "Target Impression Share" ||
                    params?.row?.bidding_strategy_type === "Maximize Conversion Value";

                return (
                    <Box
                        onClick={() => {
                            if (isDisabled) return;

                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            cursor: isDisabled ? "default" : "pointer",

                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: "100%",
                                height: "65%",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                px: isDisabled ? 0 : 2,
                                borderRadius: "6px",
                                background: "rgba(255, 255, 255, 0.01)",
                                fontSize: "0.875rem",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                boxSizing: "border-box",

                                border: isDisabled
                                    ? ""
                                    : "1px solid rgba(145, 158, 171, 0.20)",

                                transition: "all 0.15s ease",

                                "&:hover": isDisabled
                                    ? {}
                                    : {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                    },
                            }}
                        >
                            {!isDisabled
                                ? formatCurrency(value, workspace?.currency, lang, true, 2)
                                : <Box
                                    sx={{
                                        display: "flex",
                                        width: "100%",
                                        height: "100%",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <Typography color="#333C47">-</Typography>
                                </Box>
                            }
                        </Box>
                    </Box>
                );
            },
        },
        // TARGET ROAS
        {
            disableColumnMenu: true,
            field: "target_roas",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: 'left',
            headerName: "Target ROAS",
            minWidth: 110,
            editable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                >
                    Target ROAS
                </Typography>
            ),

            renderEditCell: (params) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value = (() => {
                    if (koManual?.target_roas != null) {
                        return koManual.target_roas * 100;
                    }

                    if (koManual?.target_roas_portfolio_strategy != null) {
                        return koManual.target_roas_portfolio_strategy * 100;
                    }

                    return null;
                })();

                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        sx={{
                            height: "100%",
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            defaultValue={value}
                            autoFocus
                            fullWidth
                            onChange={(event) => {
                                let value = event.target.value;
                                value = value.replace(",", ".");

                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: "target_roas",
                                    value: Number(value) / 100,
                                });
                            }}
                            sx={{
                                width: "100%",

                                "& .MuiInputBase-root": {
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                },

                                "& input": {
                                    padding: "0 12px",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                },

                            }}
                        />
                    </Box>
                );
            },

            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) =>
                        d.status === true &&
                        d.mode === "KO Bidding Manual"
                );

                const value = (() => {
                    if (koManual?.target_roas != null) {
                        return koManual.target_roas * 100;
                    }

                    if (koManual?.target_roas_portfolio_strategy != null) {
                        return koManual.target_roas_portfolio_strategy * 100;
                    }

                    return null;
                })();

                const isDisabled =
                    params?.row?.bidding_strategy_type === "Target CPA" ||
                    params?.row?.bidding_strategy_type === "Maximize Conversions" ||
                    params?.row?.bidding_strategy_type === "Target Impression Share";


                if (!koManual) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Typography color="#333C47">-</Typography>
                        </Box>
                    );
                }

                return (
                    <Box
                        onClick={() => {
                            if (isDisabled) return;

                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            cursor: isDisabled ? "default" : "pointer",
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: "100%",
                                height: "65%",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                px: isDisabled ? 0 : 2,
                                borderRadius: "6px",
                                background: "rgba(255, 255, 255, 0.01)",
                                fontSize: "0.875rem",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                boxSizing: "border-box",

                                border: isDisabled
                                    ? ""
                                    : "1px solid rgba(145, 158, 171, 0.20)",

                                transition: "all 0.15s ease",

                                "&:hover": isDisabled
                                    ? {}
                                    : {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                    },
                            }}
                        >
                            {!isDisabled

                                ? fPercent(value)
                                : <Box
                                    sx={{
                                        display: "flex",
                                        width: "100%",
                                        height: "100%",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <Typography color="#333C47">-</Typography>
                                </Box>
                            }
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "max_cpc_2",
            sortable: true,
            headerAlign: "left",
            disableColumnMenu: true,
            filterable: true,
            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                    sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                    }}
                >
                    Max CPC Evol
                </Typography>
            ),
            headerName: "Max CPC Evol",
            renderCell: ({ row }) => {
                const currentMaxCpc = row.max_cpc_2 ?? 0;
                const compareMaxCpc = row?.max_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareMaxCpc > 0) {
                    percentageChange =
                        ((currentMaxCpc - compareMaxCpc) / compareMaxCpc) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(currentMaxCpc, workspace?.currency, lang, true, 2) ?? ""}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },
            minWidth: 144,
            type: "number",
        },
        {
            field: "cost",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Ad Cost
                </Typography>
            ),

            headerName: "Ad Cost",

            renderCell: ({ row }) => {
                const currentCost = row.cost ?? 0;
                const compareCost = row?.cost_compare ?? 0;

                let percentageChange = null;

                if (compareCost > 0) {
                    percentageChange =
                        ((currentCost - compareCost) / compareCost) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentCost,
                                workspace?.currency,
                                lang,
                                true,
                                0
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse={true} />
                        </Box>
                    </Box>
                );
            },
            minWidth: 144,
            type: "number",
        },
        // AD CLICKS
        {
            field: "clicks",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Ad Clicks
                </Typography>
            ),

            headerName: "Ad Clicks",

            renderCell: ({ row }) => {
                const currentClicks = row.clicks ?? 0;
                const compareClicks = row?.clicks_compare ?? 0;

                let percentageChange = null;

                if (compareClicks > 0) {
                    percentageChange =
                        ((currentClicks - compareClicks) / compareClicks) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#333C47",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatNumber(currentClicks)}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} />
                        </Box>
                    </Box>
                );
            },

            minWidth: 144,
            type: "number",
        },
        // AVG. CPC
        {
            field: "avg_cpc",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Avg. CPC
                </Typography>
            ),

            headerName: "Avg. CPC",

            renderCell: ({ row }) => {
                const currentAvgCPC = row.avg_cpc ?? 0;
                const compareAvgCPC = row?.avg_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareAvgCPC > 0) {
                    percentageChange =
                        ((currentAvgCPC - compareAvgCPC) / compareAvgCPC) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentAvgCPC,
                                workspace?.currency,
                                lang,
                                true,
                                2
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },

            minWidth: 144,
            type: "number",
        }
    ], [workspace?.currency, lang, selectedCompareDate]);

    const handleConfirmDisable = useCallback(
        async (row: any) => {
            if (!row) return;

            const modeToDisable = confirmDisable.mode;

            try {
                if (modeToDisable === "KO Bidding Manual") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "KO Bidding Manual");

                    if (detail?.id) {
                        await disableKoBiddingManualMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Manual disabled successfully`, { variant: "success" });

                        setLocalPatches(prev => {
                            const existing = prev[row.id] ?? row;

                            return {
                                ...prev,
                                [row.id]: {
                                    ...existing,
                                    activations_details: existing.activations_details.filter(
                                        (d: any) => d.mode !== "KO Bidding Manual"
                                    ),
                                    max_cpc: null,
                                }
                            };
                        });

                    }
                }

                setConfirmDisable({ open: false, row: null, mode: null });

            } catch (err) {
                enqueueSnackbar(`Failed to disable ${modeToDisable}`, { variant: "error" });
            }
        },
        [
            confirmDisable,
            disableKoBiddingManualMaxCpc,
            setLocalPatches
        ]
    );

    const [enforceMaxCPC] = useEnforceMaxCPCMutation();
    const [updateMaxCpc] = useUpdateKoBiddingManualMutation();

    const handleUpdateMaxCPC = async (modifiedRows: any[]) => {
        try {
            const payload = modifiedRows.map((row) => ({
                id: row.id,
                workspaceId: workspace?.id,

                max_cpc: Number(row.max_cpc),
                max_cpc_portfolio_strategy: Number(row.max_cpc_portfolio_strategy),

                target_cpa: Number(row.target_cpa),
                target_cpa_portfolio_strategy: Number(row.target_cpa_portfolio_strategy),

                target_roas: Number(row.target_roas),
                target_roas_portfolio_strategy: Number(row.target_roas_portfolio_strategy),

                target_impression_share: Number(row?.target_impression_share),
                target_impression_share_position: row?.target_impression_share_position
            }));

            await updateMaxCpc(payload).unwrap();
            refetch();

            setLocalPatches((prev) => {
                const next = { ...prev };

                for (const row of modifiedRows) {
                    const rowId = row.rowId;
                    const original = data.find((r) => r.id === rowId);

                    next[rowId] = {
                        ...original,

                        activations_details: original.activations_details.map((a: any) =>
                            a.mode === "KO Bidding Manual" && a.status === true
                                ? {
                                    ...a,

                                    max_cpc: row.max_cpc ?? a.max_cpc,

                                    max_cpc_portfolio_strategy:
                                        row.max_cpc_portfolio_strategy ??
                                        a.max_cpc_portfolio_strategy,


                                    target_cpa: row.target_cpa ?? a.target_cpa,

                                    target_cpa_portfolio_strategy:
                                        row.target_cpa_portfolio_strategy ??
                                        a.target_cpa_portfolio_strategy,


                                    target_roas: row.target_roas ?? a.target_roas,

                                    target_roas_portfolio_strategy:
                                        row.target_roas_portfolio_strategy ??
                                        a.target_roas_portfolio_strategy,

                                    target_impression_share: row.target_impression_share ?? a.target_impression_share,
                                    target_impression_share_position: row.target_impression_share_position ?? a.target_impression_share_position,

                                }
                                : a
                        ),
                    };
                }

                return next;
            });

            setModifiedRows([]);

            const count = modifiedRows.length;

            enqueueSnackbar(
                `Update${count > 1 ? "s" : ""} applied successfully`,
                { variant: "success" }
            );

        } catch (error) {
            console.error("Update failed:", error);

            setLocalPatches({});
            setModifiedRows([]);
        }
    };

    const handleFinish = async (rows: any[], setResponse: (res: any[]) => void) => {

        const payload = {
            keywords: rows.map((row) => ({
                customer_id: String(row.account_id),
                account_id: String(row.account_id),
                ad_group_id: String(row.ad_group_ids[0]),
                platform: row?.platform === "GOOGLE" ? "Google Ads"
                    : row?.platform === "BING" ? "Bing Ads"
                        : row?.platform,

                keyword_id: String(row.keyword_id),
                keyword: row.keyword,
                match_type: row.match_type,
                campaign_id: String(row.campaign_ids[0]),
                max_cpc: row.max_cpc_to_enforce,
                name: workspace?.bqTable,
                country_code: row.country_code,
                message: 'Enforce max CPC from front-end'
            }))
        };


        try {
            const response = await enforceMaxCPC(payload).unwrap();
            setResponse(response);
        } catch (error) {
            console.error('Enforce error', error);
        }
    };

    const CustomToolbar = ({ selectionRows = [] }: any) => {
        return (
            <GridToolbarContainer
                ref={toolbarRef}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: 40,
                    minHeight: 40,
                    px: "20px",

                    "& .MuiButton-root": {
                        height: 32,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textTransform: "none",
                    },

                    "& .MuiAutocomplete-root": {
                        display: "flex",
                        alignItems: "center",
                    },

                    "& .MuiInputBase-root": {
                        height: 32,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        paddingTop: "0 !important",
                        paddingBottom: "0 !important",
                    },

                    "& .MuiAutocomplete-input": {
                        padding: "0 !important",
                    },

                    "& .MuiChip-root": {
                        height: 24,
                    },
                }}
            >
                {/* LEFT */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ height: "100%" }}
                >
                    <CustomTooltip title={"Show More KPIs"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showPreferences("columns")}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="mingcute:calendar-week-line"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Apply Filters"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showFilterPanel()}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="ic:round-filter-list"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Download Table"} arrow placement="top">
                        <IconButton
                            onClick={() =>
                                apiRef.current?.exportDataAsCsv({
                                    fileName: "exact_activations",
                                    utf8WithBom: true,
                                })
                            }
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2" stroke="#919EAB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    </CustomTooltip>

                </Stack>

                {/* RIGHT */}
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ height: "100%" }}
                >
                    {selectionRows.length > 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Button
                                onClick={() =>
                                    setOpenEnforceModal({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                sx={{
                                    backgroundColor:
                                        "rgba(145, 158, 171, 0.20)",
                                    borderRadius: "8px",
                                    px: 1.5,
                                }}
                                size="small"
                            >
                                Enforce Max CPC ({selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalDeactivateStrategy({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                sx={{
                                    backgroundColor:
                                        "rgba(145, 158, 171, 0.20)",
                                    borderRadius: "8px",
                                    px: 1.5,
                                }}
                                size="small"
                            >
                                Deactivate Bidding Strategy (
                                {selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalCreateStrategy({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                size="small"
                                variant="contained"
                                sx={{ mr: 1 }}
                            >
                                Activate Bidding Strategy (
                                {selectionRows.length})
                            </Button>
                        </Box>
                    )}

                    <Box
                        display="flex"
                        alignItems="center"
                        position="relative"
                    >
                        {modifiedRows.length > 0 && (
                            <Box display={"flex"} flexDirection={"row"} gap={1} px={1}>
                                <Button
                                    onClick={() => {
                                        setModifiedRows([]);
                                        setLocalPatches({});
                                    }}
                                    size="small"
                                    variant="contained"
                                >
                                    Discard ({modifiedRows.length})
                                </Button>

                                <Button
                                    onClick={() => handleUpdateMaxCPC(modifiedRows)}
                                    size="small"
                                    variant="contained"
                                    sx={{
                                        minWidth: "unset",
                                        padding: "2px 6px !important",
                                        fontSize: "12px !important",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Apply ({modifiedRows.length})
                                </Button>
                            </Box>
                        )}

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            pr={"20px"}
                        >
                            <Autocomplete
                                disablePortal
                                options={dateRangeOptions}
                                size="small"
                                disableClearable
                                onChange={(event, newValue) => {
                                    onDateRangeChange?.(newValue);
                                }}
                                getOptionLabel={(option) => option.label}
                                sx={{ width: 150 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment:
                                                selectedDateRange ? (
                                                    <Chip
                                                        label={
                                                            selectedDateRange.label
                                                        }
                                                        size="small"
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "rgba(145, 158, 171, 0.50)",
                                                            },
                                                            backgroundColor:
                                                                "rgba(145, 158, 171, 0.32)",
                                                            color:
                                                                "rgba(51, 60, 71, 1)",
                                                        }}
                                                    />
                                                ) : null,
                                        }}
                                    />
                                )}
                            />

                            <Autocomplete
                                disablePortal
                                options={comparisonPeriodOptions}
                                disableClearable
                                size="small"
                                onChange={(event, newValue) => {
                                    onCompareDateChange?.(newValue);
                                }}
                                getOptionLabel={(option) => option.label}
                                sx={{ width: 160 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment:
                                                selectedCompareDate ? (
                                                    <Chip
                                                        label={
                                                            selectedCompareDate.label
                                                        }
                                                        size=
                                                        "small"
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "rgba(145, 158, 171, 0.50)",
                                                            },
                                                            backgroundColor:
                                                                "rgba(145, 158, 171, 0.32)",
                                                            color:
                                                                "rgba(51, 60, 71, 1)",
                                                        }}
                                                    />
                                                ) : null,
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Box>
                </Box>
            </GridToolbarContainer>
        );
    };

    const [modifiedRows, setModifiedRows] = useState<any[]>([]);

    const handleRowUpdate = (newRow: any, oldRow: any) => {
        const koManual = newRow.activations_details?.find(
            (d: any) => d.mode === "KO Bidding Manual" && d.status === true
        );

        const parseNumber = (v: any) => {
            if (v === "" || v === null || v === undefined) return null;
            const n = Number(String(v).replace(",", "."));
            return Number.isFinite(n) ? n : null;
        };

        const isInvalidNumber = (v: number | null) =>
            v === null || Number.isNaN(v) || v < 0.01;

        // =====================================================
        // MAX CPC
        // =====================================================
        const nextMaxCpc = parseNumber(newRow.max_cpc);
        const oldMaxCpc = parseNumber(oldRow.max_cpc);
        const maxCpcChanged = nextMaxCpc !== oldMaxCpc;

        // =====================================================
        // TARGET CPA
        // =====================================================
        const nextTargetCpa = parseNumber(newRow.target_cpa);
        const oldTargetCpa = parseNumber(oldRow.target_cpa);
        const targetCpaChanged = nextTargetCpa !== oldTargetCpa;

        // =====================================================
        // TARGET ROAS
        // =====================================================
        const nextTargetRoas = parseNumber(newRow.target_roas);
        const oldTargetRoas = parseNumber(oldRow.target_roas);
        const targetRoasChanged = nextTargetRoas !== oldTargetRoas;

        // =====================================================
        // TARGET IMPRESSION SHARE
        // =====================================================
        const nextTargetIS = parseNumber(newRow.target_impression_share);
        const oldTargetIS = parseNumber(oldRow.target_impression_share);
        const targetISChanged = nextTargetIS !== oldTargetIS;

        // =====================================================
        // TARGET IMPRESSION SHARE POSITION
        // =====================================================
        const nextTargetISPosition = newRow.target_impression_share_position;
        const oldTargetISPosition = oldRow.target_impression_share_position;
        const targetISPositionChanged = nextTargetISPosition !== oldTargetISPosition;

        if (
            !maxCpcChanged &&
            !targetCpaChanged &&
            !targetRoasChanged &&
            !targetISChanged &&
            !targetISPositionChanged
        ) {
            return oldRow;
        }

        const isInvalidMaxCpc =
            koManual && maxCpcChanged && isInvalidNumber(nextMaxCpc);

        const isInvalidTargetCpa =
            koManual && targetCpaChanged && isInvalidNumber(nextTargetCpa);

        const isInvalidTargetRoas =
            koManual && targetRoasChanged && isInvalidNumber(nextTargetRoas);

        const isInvalidTargetIS =
            koManual && targetISChanged && isInvalidNumber(nextTargetIS);

        const isInvalidTargetISPosition =
            koManual && targetISPositionChanged && !nextTargetISPosition;

        const isInvalid =
            isInvalidMaxCpc ||
            isInvalidTargetCpa ||
            isInvalidTargetRoas ||
            isInvalidTargetIS ||
            isInvalidTargetISPosition;

        setModifiedRows((prev: any) => {
            const filtered = prev.filter((r: any) => r.rowId !== newRow.id);
            const existing = prev.find((r: any) => r.rowId === newRow.id);

            if (isInvalid) {
                return filtered;
            }

            const patch: any = {
                ...existing,
                rowId: newRow.id,
                id: koManual?.id,
            };

            if (maxCpcChanged) {
                const isPortfolio = koManual?.max_cpc == null;

                const key = isPortfolio
                    ? "max_cpc_portfolio_strategy"
                    : "max_cpc";

                patch[key] = nextMaxCpc;
            }

            if (targetCpaChanged) {
                const isPortfolio = koManual?.target_cpa == null;

                const key = isPortfolio
                    ? "target_cpa_portfolio_strategy"
                    : "target_cpa";

                patch[key] = nextTargetCpa;
            }

            if (targetRoasChanged) {
                const isPortfolio = koManual?.target_roas == null;

                const key = isPortfolio
                    ? "target_roas_portfolio_strategy"
                    : "target_roas";

                patch[key] = nextTargetRoas;
            }

            if (targetISChanged) {
                patch["target_impression_share"] = nextTargetIS;
            }

            if (targetISPositionChanged) {
                patch["target_impression_share_position"] = nextTargetISPosition;
            }

            return [...filtered, patch];
        });

        const updatedRow = {
            ...newRow,
            activations_details: newRow.activations_details?.map((a: any) => {
                if (a.id !== koManual?.id) return a;

                const isPortfolioMax = a.max_cpc == null;
                const isPortfolioCpa = a.target_cpa == null;
                const isPortfolioRoas = a.target_roas == null;

                return {
                    ...a,

                    ...(maxCpcChanged && {
                        max_cpc: !isPortfolioMax ? nextMaxCpc : a.max_cpc,
                        max_cpc_portfolio_strategy: isPortfolioMax
                            ? nextMaxCpc
                            : a.max_cpc_portfolio_strategy,
                    }),

                    ...(targetCpaChanged && {
                        target_cpa: !isPortfolioCpa ? nextTargetCpa : a.target_cpa,
                        target_cpa_portfolio_strategy: isPortfolioCpa
                            ? nextTargetCpa
                            : a.target_cpa_portfolio_strategy,
                    }),

                    ...(targetRoasChanged && {
                        target_roas: !isPortfolioRoas ? nextTargetRoas : a.target_roas,
                        target_roas_portfolio_strategy: isPortfolioRoas
                            ? nextTargetRoas
                            : a.target_roas_portfolio_strategy,
                    }),

                    ...(targetISChanged && {
                        target_impression_share: nextTargetIS,
                    }),

                    ...(targetISPositionChanged && {
                        target_impression_share_position: nextTargetISPosition,
                    }),
                };
            }),
            isEdited: true,
            isInvalid,
        };

        return updatedRow;
    };

    const mergedRows = useMemo(() => {
        return data.map((row) => {
            const patch = localPatches[row.id];
            if (!patch) return row;

            const existing = row.activations_details || [];
            const patched = patch.activations_details || [];

            const mergedExisting = existing.map((a: any) => {
                const found = patched.find((p: any) => p.id === a.id);
                return found ? { ...a, ...found } : a;
            });

            const newOnes = patched.filter(
                (p: any) => !existing.some((a: any) => a.id === p.id)
            );

            return {
                ...row,
                activations_details: [...mergedExisting, ...newOnes],
            };
        });
    }, [data, localPatches]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem("columnVisibilityModelActivationsCamapaigns");
        return saved ? JSON.parse(saved) : {
            campaign_id: false,
            ad_group_id: false,
            target_cpa: false,
            target_roas: false,
            target_impression_share: false,
            target_impression_share_position: false,
            platform: false
        };
    });

    const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        localStorage.setItem("columnVisibilityModelActivationsCamapaigns", JSON.stringify(newModel));
    }, []);

    return (
        <Box
            sx={{
                height: '715px',
                width: '100%',
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainer": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%!important",
                },
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainerContent": {
                    display: "flex",
                    flex: 1,
                },
                "& .MuiDataGrid-columnHeader": {
                    backgroundColor: 'rgba(250, 251, 251)',
                    borderLeft: "1px solid #e9ecee"
                },
                "& .keyword-header-wrapper": {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                },
                position: "relative",
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    borderLeft: "1px solid #e9ecee"
                },
                "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                },
                "& .MuiDataGrid-columnSeparator": {
                    color: "rgba(255,255,255,0.12)",
                    visibility: "visible",
                },

                "& .MuiDataGrid-cell:not(:last-of-type)": {
                    borderRight: "1px solid rgba(255,255,255,0.06)",
                },

                "& .css-ly985t-MuiDataGrid-root .MuiTablePagination-toolbar": {
                    color: '#919EAB!important'
                },
                "& .edited-row": {
                    backgroundColor: '#f1ebfb!important'
                },
                "& .MuiDataGrid-row.row-invalid": {
                    backgroundColor: "#ffe5e5 !important",
                },
                "& .MuiDataGrid-row:has(.Mui-error)": {
                    backgroundColor: "#ffe5e5 !important",
                },
                "& .MuiDataGrid-cell:has(.Mui-error) .MuiDataGrid-editInputCell input": {
                    backgroundColor: "#ffe5e5 !important",
                    border: '0px !important',
                },
                "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    color: '#919EAB!important'
                },
                "& .css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input": {
                    borderRadius: '6px',
                    border: "1px solid rgba(145, 158, 171, 0.20)",
                    color: "#919EAB!important"
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root": {
                    color: "#919EAB!important",
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                    color: "#d0d5da!important",
                },
                "& .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none !important",
                },
                ".css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader, .css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-filler, .css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-scrollbarFiller": {
                    backgroundColor: '#fafbfb!important'
                }
            }}
        >
            <DataGridPro
                rowHeight={57}
                processRowUpdate={handleRowUpdate}
                apiRef={apiRef}
                getRowId={(r) => r.id}
                columnHeaderHeight={50}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onPaginationModelChange={({ page, pageSize }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => <CustomToolbar selectionRows={selectionRows} />,
                    filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection: any) => {
                    const selectedIds = Array.isArray(newSelection) ? newSelection : [];
                    setSelectionRows(
                        mergedRows.filter((row) => selectedIds.includes(row.id))
                    );
                }}
                getRowClassName={(params) =>
                    [
                        params.row.isEdited ? "edited-row" : "",
                        params.row.isInvalid ? "row-invalid" : ""
                    ].join(" ")
                }
                isCellEditable={(params) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) =>
                            d.status === true &&
                            d.mode === "KO Bidding Manual"
                    );

                    if (!koManual) return false;

                    const strategy = (params?.row?.bidding_strategy_type || "").trim();

                    const strategyFieldsMap: Record<string, string[]> = {
                        "Target ROAS": ["max_cpc", "target_roas"],
                        "Maximize Conversion Value": ["max_cpc", "target_roas"],

                        "Target CPA": ["max_cpc", "target_cpa"],
                        "Maximize Conversions": ["max_cpc", "target_cpa"],

                        "Target Impression Share": ["max_cpc", "target_impression_share", "target_impression_share_position"]
                    };

                    const allowedFields =
                        strategyFieldsMap[strategy] ?? ["max_cpc"];

                    return allowedFields.includes(params.field);
                }}
                disableRowSelectionOnClick
                columns={columns}
                sx={{
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e9ecee",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                    },
                }}
            />

            <ModalCreateStrategyCampaigns
                refetch={refetch}
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ModalDeactivateStrategy
                refetch={refetch}
                open={modalDeactivateStrategy.open}
                row={modalDeactivateStrategy.row}
                onClose={() => setModalDeactivateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />

            <EnforceMaxCpcModal
                onFinish={handleFinish}
                open={openEnforceModal.open}
                onClose={() => setOpenEnforceModal({ open: false, row: null })}
                selectedRows={openEnforceModal.row}
            />
        </Box >
    );
}